/**
 * <postcode-checker> — unified PDP postcode checker.
 * Branches on data-fulfillment-type:
 * - "standard": nearest store + try-in-store/pickup status (window.omfGeo,
 *   assets/geolocation.js) + delivery fee from JSRates. Stock levels are
 *   shown by a separate section, not here.
 * - "dropship": delivery fee/serviceability from JSRates, no store section
 *
 * Delivery fees come from the JSRates API — the same calculateShippingRates
 * function checkout runs — so tiers, BiB, sofa rates and promos only ever
 * need updating in JSRates. Artiss isn't routed through JSRates (own Shopify
 * shipping profile), so it still uses the Cloudflare Worker.
 *
 * Replaces the old theme's separate stock-availability-widget-legacy.js and
 * dropship-shipping-estimator.js controllers.
 */

if (!customElements.get('postcode-checker')) {
const JSRATES_URL = '/apps/jsrates/rates';
const WORKER_VENDORS = new Set(['Artiss']);
const DSZ_FALLBACK_RATE = 6000; // cents — Artiss fee shown when the worker/DSZ can't be reached

// Cents → "$99", "$49.90", or "Free"
function formatFee(cents) {
  if (!cents) return 'Free';
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

function titleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

class PostcodeChecker extends HTMLElement {
  // Setup lives in connectedCallback (not the constructor) because this
  // theme's variant-change handling can tear down and reinsert a block's
  // markup (including its <script> data blobs) without necessarily creating
  // a brand-new element — connectedCallback reliably re-fires every time the
  // element (re)connects, so state and data always get refreshed correctly.
  connectedCallback() {
    this.fulfillmentType = this.dataset.fulfillmentType;
    this.form = this.querySelector('.postcode-checker__form');
    this.input = this.querySelector('.postcode-checker__input');
    this.submitButton = this.querySelector('.postcode-checker__submit');
    this.results = this.querySelector('[data-results]');
    this.suburbList = null;
    this.errorEl = null;
    this.currentSuburbLabel = '';
    this.currentVariantId = parseInt(this.dataset.selectedVariantId, 10);
    this.lastStore = null;
    this.lastStandard = null;
    this.lastSuburb = null;
    this.requestId = 0;

    if (this.fulfillmentType === 'dropship') {
      const dataEl = document.getElementById(this.dataset.variantDataId);
      if (dataEl) {
        try {
          const parsed = JSON.parse(dataEl.textContent);
          this.variants = parsed.variants;
          this.workerUrl = parsed.workerUrl;
        } catch (e) {
          console.error('Postcode checker: failed to parse variant data', e);
        }
      }
    } else if (this.fulfillmentType === 'standard') {
      const regionalEl = document.getElementById(this.dataset.regionalDataId);
      if (regionalEl) {
        try {
          this.regionalVariants = JSON.parse(regionalEl.textContent).variants;
        } catch (e) {
          console.error('Postcode checker: failed to parse variant data', e);
        }
      }
    }

    if (!this.form || !this.input || !this.submitButton || !this.results) return;

    this.onSubmitBound = this.onSubmit.bind(this);
    this.onInputBound = () => {
      this.lastSuburb = null;
      this.clearError();
      this.removeSuburbList();
      this.hideResults();
    };

    this.form.addEventListener('submit', this.onSubmitBound);
    this.input.addEventListener('input', this.onInputBound);

    this.subscribeToVariantChange();
  }

  disconnectedCallback() {
    if (this.form && this.onSubmitBound) this.form.removeEventListener('submit', this.onSubmitBound);
    if (this.input && this.onInputBound) this.input.removeEventListener('input', this.onInputBound);
    if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
  }

  subscribeToVariantChange() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) return;

    this.unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        if (!event.data.variant) return;

        this.currentVariantId = event.data.variant.id;

        if (this.fulfillmentType === 'dropship') {
          if (this.lastSuburb) this.checkDropship(this.lastSuburb, this.currentSuburbLabel);
        } else if (this.fulfillmentType === 'standard' && this.lastStandard) {
          // Price, BiB/sofa category and promo threshold can all differ per variant
          this.renderStandardWithShipping();
        }
      }
    );
  }

  async onSubmit(e) {
    e.preventDefault();
    this.clearError();
    this.removeSuburbList();

    const postcode = this.input.value.trim().replace(/\D/g, '').slice(0, 4);

    if (!/^\d{4}$/.test(postcode)) {
      this.showError('Please enter a valid 4-digit postcode');
      return;
    }

    this.submitButton.disabled = true;

    try {
      if (!window.omfGeo) throw new Error('Location service unavailable. Please refresh and try again.');

      const matches = await window.omfGeo.getPostcodeMatches(postcode);

      if (!matches || matches.length === 0) {
        this.showError('Postcode not found. Please check and try again.');
        return;
      }

      if (matches.length === 1) {
        this.selectSuburb(matches[0]);
      } else {
        this.showSuburbList(matches);
      }
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  // ── Suburb disambiguation ──────────────────────────────────────────────

  showSuburbList(matches) {
    this.removeSuburbList();

    const list = document.createElement('div');
    list.className = 'postcode-checker__suburb-list';

    matches.forEach((match) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'postcode-checker__suburb-item';

      const suburbSpan = document.createElement('span');
      suburbSpan.textContent = titleCase(match.Suburb);

      const stateSpan = document.createElement('span');
      stateSpan.className = 'postcode-checker__suburb-item-state';
      stateSpan.textContent = `${match.State} ${match.Postocode}`;

      item.append(suburbSpan, stateSpan);
      item.addEventListener('click', () => {
        this.input.value = `${titleCase(match.Suburb)}, ${match.State} ${match.Postocode}`;
        this.removeSuburbList();
        this.selectSuburb(match);
      });

      list.appendChild(item);
    });

    this.form.after(list);
    this.suburbList = list;
  }

  removeSuburbList() {
    if (this.suburbList) {
      this.suburbList.remove();
      this.suburbList = null;
    }
  }

  selectSuburb(suburb) {
    this.currentSuburbLabel = titleCase(suburb.Suburb);
    this.lastSuburb = suburb;
    if (this.fulfillmentType === 'standard') {
      this.checkStandard(suburb);
    } else {
      this.checkDropship(suburb, this.currentSuburbLabel);
    }
  }

  // ── JSRates ──────────────────────────────────────────────────────────────

  // Runs the checkout rate function for one unit of the variant. Returns the
  // first rate ({ service_name, total_price (cents), description }), or null
  // when JSRates offers no delivery to this address (= not serviceable).
  async fetchJsRatesRate(suburb, variantData) {
    const lat = parseFloat(suburb.Latitude);
    const lng = parseFloat(suburb.Longitude);
    const res = await fetch(JSRATES_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rate: {
          destination: {
            country: 'AU',
            postal_code: suburb.Postocode,
            city: suburb.Suburb,
            province: suburb.State,
            latitude: isNaN(lat) ? null : lat,
            longitude: isNaN(lng) ? null : lng,
          },
          items: [
            {
              vendor: this.dataset.vendor,
              product_id: parseInt(this.dataset.productId, 10),
              variant_id: variantData.id,
              price: variantData.price,
              quantity: 1,
              requires_shipping: true,
            },
          ],
          currency: 'AUD',
          locale: 'en',
        },
      }),
    });
    if (!res.ok) throw new Error(`JSRates HTTP ${res.status}`);
    const data = await res.json();
    return data.rates?.[0] ?? null;
  }

  // ── Shared UI helpers ────────────────────────────────────────────────────

  setOption(name, available, text) {
    const option = this.querySelector(`[data-option="${name}"]`);
    if (!option) return;
    const dot = option.querySelector('[data-dot]');
    const title = option.querySelector('[data-title]');
    if (dot) dot.classList.toggle('is-available', !!available);
    if (title && text !== undefined) title.textContent = text;
  }

  setLoading(on) {
    this.results.classList.toggle('is-loading', on);
  }

  showResults() {
    this.results.classList.add('is-visible');
  }

  hideResults() {
    this.results.classList.remove('is-visible');
  }

  showError(message) {
    this.clearError();
    const el = document.createElement('div');
    el.className = 'postcode-checker__error';
    el.textContent = message;
    this.form.after(el);
    this.errorEl = el;
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.remove();
      this.errorEl = null;
    }
  }

  // ── Standard path ───────────────────────────────────────────────────────

  async checkStandard(suburb) {
    this.clearError();
    this.showResults();
    this.setLoading(true);

    try {
      if (!window.omfGeo) throw new Error('Location service unavailable');

      const lat = parseFloat(suburb.Latitude);
      const lng = parseFloat(suburb.Longitude);

      // PO Box postcodes often have bad/negative coordinate data.
      if (isNaN(lat) || isNaN(lng) || lng <= 0) {
        this.setLoading(false);
        this.hideResults();
        this.showError('This appears to be a PO Box postcode. Please enter a street delivery postcode.');
        return;
      }

      const result = await window.omfGeo.getNearestStore(lat, lng);
      if (!result) throw new Error('No stores found');

      this.lastStandard = { store: result.store, suburb };
      this.lastStore = result.store;
      await this.renderStandardWithShipping();
      this.setLoading(false);
    } catch (err) {
      console.error('Postcode checker: error checking availability', err);
      this.setLoading(false);
      this.renderStandardError();
    }
  }

  // Fetches the current variant's fee from JSRates and renders the standard
  // results. shipping: rate object, null = not deliverable (JSRates offers no
  // rate — postcode exclusions live in JSRates), undefined = fee unknown
  // (request failed). Responses for a since-changed variant are dropped.
  async renderStandardWithShipping() {
    const { store, suburb } = this.lastStandard;
    const requestId = ++this.requestId;
    let shipping;

    try {
      const variantData = this.getRegionalVariantData(this.currentVariantId);
      if (!variantData) throw new Error(`No variant data for ${this.currentVariantId}`);
      shipping = await this.fetchJsRatesRate(suburb, variantData);
    } catch (err) {
      console.error('Postcode checker: JSRates request failed', err);
    }

    if (requestId !== this.requestId) return;
    this.renderStandardResults(store, shipping, suburb, shipping === null);
  }

  renderStandardResults(store, shipping, suburb, deliveryExcluded) {
    const storeName = this.querySelector('[data-store-name]');
    const storeAddress = this.querySelector('[data-store-address]');
    if (storeName) {
      storeName.textContent = `OMF ${store.name}`;
      storeName.classList.remove('is-error');
    }
    if (storeAddress) storeAddress.textContent = `${store.address}, ${store.city} ${store.postcode}`.trim();

    const directionsLink = this.querySelector('[data-directions-link]');
    if (directionsLink) {
      directionsLink.href = store.pageUrl || '/pages/stores';
      directionsLink.hidden = false;
    }

    const rawPhone = store.phone || '';
    const telHref = rawPhone ? `tel:${rawPhone.replace(/\D/g, '')}` : '';
    this.querySelectorAll('[data-phone-display]').forEach((el) => {
      el.textContent = rawPhone;
    });

    const phoneLink = this.querySelector('[data-phone-link]');
    if (phoneLink) {
      phoneLink.href = telHref;
      phoneLink.hidden = !rawPhone;
    }

    const isOmfVendor =
      this.dataset.vendor === 'Original Mattress Factory' ||
      this.dataset.vendor === 'Original Mattress Factory Australia';

    this.setOption(
      'try-instore',
      isOmfVendor,
      isOmfVendor
        ? this.dataset.tryInstoreAvailable || 'Available to try in-store'
        : this.dataset.tryInstoreUnavailable || 'Not available to try in-store'
    );

    this.setOption(
      'pickup',
      isOmfVendor,
      isOmfVendor ? 'Free pickup available' : this.dataset.pickupUnavailable || 'Not available for Click & Collect'
    );

    // Delivery fee line — straight from JSRates (hidden when not deliverable)
    const feeRow = this.querySelector('[data-option="delivery-fee-line"]');
    if (feeRow) feeRow.hidden = deliveryExcluded;
    const isFree = !!shipping && !shipping.total_price;
    this.setOption(
      'delivery-fee-line',
      true,
      isFree
        ? 'Free Delivery'
        : shipping
          ? `Delivery Fee: ${formatFee(shipping.total_price)}`
          : 'Delivery fee calculated at checkout'
    );
    feeRow?.querySelector('[data-title]')?.classList.toggle('is-free', isFree);

    const suburbLabel = suburb ? titleCase(suburb.Suburb) : '';
    const deliveryText = [suburbLabel, suburb?.Postocode].filter(Boolean).join(' ');
    const deliveryCallback = this.querySelector('[data-option="delivery-callback"]');

    if (deliveryExcluded) {
      this.setOption('delivery', false, 'Delivery unavailable - please contact your nearest store');
      if (deliveryCallback) deliveryCallback.hidden = true;
    } else {
      if (deliveryCallback) deliveryCallback.hidden = false;
      this.setOption('delivery', true, deliveryText ? `Available to order to ${deliveryText}` : 'Available to order');
      this.setOption(
        'delivery-callback',
        true,
        shipping?.description || 'Our team will call within 1 business day to book your delivery'
      );
    }

    const callCta = this.querySelector('[data-call-cta]');
    if (callCta) {
      if (isOmfVendor && rawPhone) {
        callCta.href = telHref;
        callCta.hidden = false;
      } else {
        callCta.hidden = true;
      }
    }
  }

  renderStandardError() {
    this.showResults();
    const storeName = this.querySelector('[data-store-name]');
    if (storeName) {
      storeName.textContent = 'Error checking availability';
      storeName.classList.add('is-error');
    }
    const directionsLink = this.querySelector('[data-directions-link]');
    if (directionsLink) directionsLink.hidden = true;
    const phoneLink = this.querySelector('[data-phone-link]');
    if (phoneLink) phoneLink.hidden = true;
    this.querySelectorAll('[data-dot]').forEach((dot) => dot.classList.remove('is-available'));
    const feeRow = this.querySelector('[data-option="delivery-fee-line"]');
    if (feeRow) feeRow.hidden = true;
  }

  getRegionalVariantData(id) {
    return (this.regionalVariants || []).find((v) => v.id === id) || null;
  }

  // ── Dropship path ───────────────────────────────────────────────────────

  getVariantData(id) {
    return (this.variants || []).find((v) => v.id === id) || null;
  }

  async checkDropship(suburb, suburbLabel) {
    const variantData = this.getVariantData(this.currentVariantId);
    if (!variantData) return;

    const postcode = suburb.Postocode;
    const requestId = ++this.requestId;
    this.clearError();
    this.showResults();
    this.setLoading(true);

    try {
      let result;
      if (WORKER_VENDORS.has(this.dataset.vendor)) {
        result = await this.fetchWorkerRate(postcode, variantData);
      } else {
        const rate = await this.fetchJsRatesRate(suburb, variantData);
        result = { serviceable: !!rate, rate: rate?.total_price ?? 0 };
      }
      if (requestId !== this.requestId) return;
      this.renderDropshipResult(result.serviceable, result.rate, result.free, postcode, suburbLabel);
    } catch (err) {
      console.error('Postcode checker: delivery check failed', err);
      if (requestId !== this.requestId) return;
      if (WORKER_VENDORS.has(this.dataset.vendor)) {
        this.renderDropshipResult(true, DSZ_FALLBACK_RATE, false, postcode, suburbLabel);
      } else {
        this.renderDropshipError();
      }
    } finally {
      if (requestId === this.requestId) this.setLoading(false);
    }
  }

  // Artiss only — priced by the DSZ API via the Cloudflare Worker
  async fetchWorkerRate(postcode, variantData) {
    if (!this.workerUrl) throw new Error('Worker URL missing');
    const res = await fetch(this.workerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        postcode,
        vendor: this.dataset.vendor,
        sku: variantData.sku,
        rate_category: variantData.rateCategory,
      }),
    });
    if (!res.ok) throw new Error(`Worker HTTP ${res.status}`);
    return res.json();
  }

  renderDropshipResult(serviceable, rate, free, postcode, suburbLabel) {
    const feeRow = this.querySelector('[data-option="delivery-fee"]');
    const cta = this.querySelector('[data-dropship-cta]');

    if (!serviceable) {
      const location = suburbLabel ? `${suburbLabel} ${postcode}` : postcode;
      this.setOption('delivery', false, `Delivery is not available to ${location}.`);
      if (feeRow) feeRow.hidden = true;
      if (cta) {
        cta.textContent = 'Please contact your nearest store for delivery enquiries.';
        cta.hidden = false;
      }
      return;
    }

    this.setOption(
      'delivery',
      true,
      free || !rate ? 'Free delivery available to your postcode.' : 'Delivery available to your postcode.'
    );

    if (cta) {
      cta.textContent = 'Delivered directly from our supplier to your door.';
      cta.hidden = false;
    }

    if (free || !rate) {
      if (feeRow) feeRow.hidden = true;
    } else if (feeRow) {
      const dollars = (rate / 100).toFixed(2);
      this.setOption('delivery-fee', true, `Delivery fee: $${dollars}`);
      feeRow.hidden = false;
    }
  }

  renderDropshipError() {
    this.setOption('delivery', false, 'Unable to check delivery. Please try again.');
    const feeRow = this.querySelector('[data-option="delivery-fee"]');
    if (feeRow) feeRow.hidden = true;
    const cta = this.querySelector('[data-dropship-cta]');
    if (cta) cta.hidden = true;
  }
}

customElements.define('postcode-checker', PostcodeChecker);
}
